import React, { useEffect, useState } from "react";
import Pagination from "../../../components/Pagination";
import api from "../../../../utils/axios";
import { useEscapeClose } from "../../../../utils/SamuiUtils";
import {
  useModalKeyboardNavigation,
  getKeyboardInstructions,
} from "../../../../utils/ModalKeyboardNavigation";

export default function QuoteModal({
  isOpen,
  onClose,
  onSelect,
  selectedQuoteId,
}) {
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEscapeClose(isOpen, onClose);

  // คำนวณ global index จาก currentPage และ local index
  const globalSelectedIndex = (currentPage - 1) * itemsPerPage + selectedRowIndex;

  useModalKeyboardNavigation(
    isOpen,
    filteredQuotes,
    globalSelectedIndex,
    (newGlobalIndex) => {
      // แปลง global index เป็น page และ local index
      const page = Math.floor(newGlobalIndex / itemsPerPage) + 1;
      const localIndex = newGlobalIndex % itemsPerPage;
      setCurrentPage(page);
      setSelectedRowIndex(localIndex);
    },
    null,
    onClose,
    {
      customEnterHandler: () => {
        if (globalSelectedIndex >= 0 && globalSelectedIndex < filteredQuotes.length) {
          handleSelect(filteredQuotes[globalSelectedIndex]);
        }
      },
      includeHighlightedOnEnter: true,
    }
  );

  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);
    const compId = localStorage.getItem("company") || 1;

    api
      .get(`/get-quotes?Comp_Id=${compId}`)
      .then((res) => {
        if (res.data.status === "OK") {
          const data = (res.data.result || []).filter(
            (q) => q.Doc_Status === 2
          );
          setQuotes(data);
          setFilteredQuotes(data);
        } else {
          setQuotes([]);
          setFilteredQuotes([]);
        }
      })
      .catch(() => {
        setQuotes([]);
        setFilteredQuotes([]);
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!selectedQuoteId) return;
    if (filteredQuotes.length === 0) return;

    const timer = setTimeout(() => {
      const index = filteredQuotes.findIndex(
        (q) => String(q.Doc_Id) === String(selectedQuoteId)
      );

      if (index >= 0) {
        const page = Math.floor(index / itemsPerPage) + 1;
        setCurrentPage(page);
        // selectedRowIndex เป็น local index ในหน้าที่แสดง (0-based ใน paginatedList)
        setSelectedRowIndex(index % itemsPerPage);
      }
    }, 120);

    return () => clearTimeout(timer);
  }, [isOpen, filteredQuotes, selectedQuoteId, itemsPerPage]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setQuotes([]);
      setFilteredQuotes([]);
      setSelectedRowIndex(0);
      setCurrentPage(1);
    }
  }, [isOpen]);

  useEffect(() => {
    const term = searchTerm.toLowerCase();

    const filtered = quotes.filter((q) => {
      return (
        (q.Doc_No || "").toLowerCase().includes(term) ||
        (q.CustomerName || "").toLowerCase().includes(term) ||
        (q.AR_ContactName || "").toLowerCase().includes(term) ||
        (q.AR_Tel || "").toLowerCase().includes(term) ||
        (q.ProvinceName || "").toLowerCase().includes(term)
      );
    });

    setFilteredQuotes(filtered);

    if (selectedQuoteId) {
      const idx = filtered.findIndex(
        (q) => String(q.Doc_Id) === String(selectedQuoteId)
      );

      if (idx >= 0) {
        const page = Math.floor(idx / itemsPerPage) + 1;
        setCurrentPage(page);
        // selectedRowIndex เป็น local index ในหน้าที่แสดง (0-based ใน paginatedList)
        setSelectedRowIndex(idx % itemsPerPage);
        return;
      }
    }

    setSelectedRowIndex(0);
    setCurrentPage(1);
  }, [searchTerm, quotes, selectedQuoteId, itemsPerPage]);

  // ตรวจสอบว่า currentPage ไม่เกินจำนวนหน้าที่มีจริง
  useEffect(() => {
    if (filteredQuotes.length === 0) {
      setCurrentPage(1);
      return;
    }
    const maxPage = Math.max(1, Math.ceil(filteredQuotes.length / itemsPerPage));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
      setSelectedRowIndex(0);
    }
  }, [filteredQuotes.length, itemsPerPage, currentPage]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedList = filteredQuotes.slice(startIndex, endIndex);

  const handleSelect = (quote) => {
    if (!quote) return;
    onSelect(quote);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`modal fade ${isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
      >
        <div
          className="modal-dialog modal-lg"
          style={{ maxWidth: "50%" }}
        >
          <div className="modal-content border-0 shadow-lg">
            <div
              className="modal-header"
              style={{ backgroundColor: "#E13F57", color: "white" }}
            >
              <h4 className="modal-title">
                <b>เลือกใบเสนอราคา</b>
              </h4>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="ค้นหา เลขที่ / ชื่อลูกค้า..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div
                style={{
                  maxHeight: "450px",
                  overflowY: "auto",
                  overflowX: "hidden",
                  width: "100%",
                }}
              >
                <table
                  className="table table-hover"
                  style={{
                    width: "100%",
                    minWidth: "max-content",
                    fontSize: "14px",
                    tableLayout: "auto",
                    borderCollapse: "collapse",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#E13F57" }}>
                      <th
                        className="text-center"
                        style={{
                          width: "18%",
                          color: "white",
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                        }}
                      >
                        เลขที่ใบเสนอราคา
                      </th>
                      <th
                        style={{
                          color: "white",
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                        }}
                      >
                        ลูกค้า
                      </th>
                      <th
                        style={{
                          color: "white",
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                        }}
                      >
                        ผู้ติดต่อ
                      </th>
                      <th
                        style={{
                          color: "white",
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                        }}
                      >
                        เบอร์โทร
                      </th>
                      <th
                        style={{
                          color: "white",
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                        }}
                      >
                        จังหวัด
                      </th>
                      <th
                        className="text-center"
                        style={{
                          width: "10%",
                          color: "white",
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                        }}
                      >
                        การเลือก
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="text-center py-3 text-muted"
                        >
                          กำลังโหลดข้อมูล...
                        </td>
                      </tr>
                    ) : paginatedList.length > 0 ? (
                      paginatedList.map((q, idx) => {
                        // ใช้ local index ใน paginatedList สำหรับ highlight
                        const isHighlighted =
                          String(q.Doc_Id) ===
                          String(selectedQuoteId) ||
                          idx === selectedRowIndex;

                        const formatPhone = (tel) => {
                          if (!tel) return "-";
                          const digits = tel
                            .replace(/\D/g, "")
                            .slice(0, 10);
                          if (digits.length <= 3) return digits;
                          if (digits.length <= 6)
                            return `${digits.slice(
                              0,
                              3
                            )}-${digits.slice(3)}`;
                          return `${digits.slice(
                            0,
                            3
                          )}-${digits.slice(3, 6)}-${digits.slice(
                            6
                          )}`;
                        };

                        return (
                          <tr
                            key={q.Doc_Id}
                            onClick={() => handleSelect(q)}
                            style={{
                              cursor: "pointer",
                              backgroundColor: isHighlighted
                                ? "rgba(225,63,87,0.2)"
                                : "",
                            }}
                          >
                            <td
                              className="text-center"
                              style={{ whiteSpace: "nowrap" }}
                            >
                              {q.Doc_No || "-"}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {q.CustomerName ||
                                q.AR_Name ||
                                "-"}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {q.AR_ContactName || "-"}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {formatPhone(q.AR_Tel)}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {q.ProvinceName || "-"}
                            </td>
                            <td
                              className="text-center"
                              style={{ whiteSpace: "nowrap" }}
                            >
                              <button
                                className="btn btn-sm btn-success"
                                style={{ fontSize: "14px" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelect(q);
                                }}
                              >
                                เลือก
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="6"
                          className="text-center py-3 text-muted"
                        >
                          ไม่พบข้อมูล
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

              </div>

              <div className="mt-2 border-top pt-2">
                <Pagination
                  totalItems={filteredQuotes.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={(page) => {
                    setCurrentPage(page);
                    setSelectedRowIndex(0); // Reset selection เมื่อเปลี่ยนหน้า
                  }}
                  onItemsPerPageChange={(val) => {
                    setItemsPerPage(val);
                    // คำนวณจำนวนหน้าที่มีจริง
                    const maxPage = Math.max(1, Math.ceil(filteredQuotes.length / val));
                    // ตรวจสอบว่า currentPage ไม่เกินจำนวนหน้าที่มีจริง
                    const newPage = currentPage > maxPage ? maxPage : 1;
                    setCurrentPage(newPage);
                    setSelectedRowIndex(0); // Reset selection เมื่อเปลี่ยน items per page
                  }}
                />
              </div>
            </div>

            <div className="modal-footer">
              <div
                className="text-left text-muted mr-auto"
                style={{ fontSize: "12px" }}
              >
                {getKeyboardInstructions("ใบเสนอราคา", true)}
              </div>
              <button className="btn btn-secondary" onClick={onClose}>
                ปิด
              </button>
            </div>
          </div>
        </div>
      </div>

      {isOpen && <div className="modal-backdrop fade show"></div>}
    </>
  );
}
